// Ad Types with icons, descriptions, and style guides
export const AD_TYPES = [
  {
    id: "product_showcase",
    name: "Product Showcase",
    icon: "📦",
    description: "Clean, focused product presentation",
    style: "Professional lighting, minimal backgrounds, product-focused composition",
    examples: ["Studio product shot", "White background display", "Feature highlight"]
  },
  {
    id: "lifestyle",
    name: "Lifestyle",
    icon: "🌟",
    description: "Product in real-world context",
    style: "Natural settings, authentic moments, relatable scenarios",
    examples: ["Morning coffee scene", "Outdoor adventure", "Home comfort"]
  },
  {
    id: "minimalist",
    name: "Minimalist",
    icon: "⬜",
    description: "Clean, simple aesthetics",
    style: "Negative space, simple compositions, subtle colors",
    examples: ["Single product focus", "Geometric layouts", "Monochromatic schemes"]
  },
  {
    id: "bold",
    name: "Bold & Vibrant",
    icon: "🎨",
    description: "Eye-catching, energetic visuals",
    style: "Vibrant colors, dynamic angles, high contrast",
    examples: ["Neon accents", "Action shots", "Pop art influences"]
  },
  {
    id: "luxury",
    name: "Luxury",
    icon: "💎",
    description: "Premium, sophisticated feel",
    style: "Elegant styling, rich textures, refined lighting",
    examples: ["Gold accents", "Velvet backgrounds", "Crystal reflections"]
  },
  {
    id: "seasonal",
    name: "Seasonal",
    icon: "🎄",
    description: "Time-based festive campaigns",
    style: "Holiday themes, seasonal colors, festive decorations",
    examples: ["Christmas warmth", "Summer vibes", "Fall harvest"]
  },
  {
    id: "event_launch",
    name: "Event / Launch",
    icon: "🎉",
    description: "Product launches, announcements",
    style: "Dynamic lighting, bold focus, excitement and energy",
    examples: ["Grand reveal spotlight", "Crowd energy", "Confetti celebration"]
  },
  {
    id: "behind_scenes",
    name: "Behind-the-Scenes",
    icon: "🎬",
    description: "Authentic, raw brand storytelling",
    style: "Candid moments, natural lighting, documentary feel",
    examples: ["Making of", "Team at work", "Process reveal"]
  },
  {
    id: "nature_eco",
    name: "Nature / Eco",
    icon: "🌿",
    description: "Organic, sustainable, fresh aesthetics",
    style: "Natural light, greenery, earthy tones",
    examples: ["Forest background", "Wooden textures", "Sunlight through leaves"]
  }
];

// Tones with sub-tones
export const TONES = [
  {
    id: "professional",
    name: "Professional",
    subTones: ["Formal", "Authoritative", "Corporate", "Expert"]
  },
  {
    id: "friendly",
    name: "Friendly",
    subTones: ["Casual", "Playful", "Warm", "Approachable"]
  },
  {
    id: "inspiring",
    name: "Inspiring",
    subTones: ["Motivational", "Aspirational", "Empowering", "Uplifting"]
  },
  {
    id: "urgent",
    name: "Urgent",
    subTones: ["Limited Time", "Exclusive", "Don't Miss Out", "Act Now"]
  },
  {
    id: "humorous",
    name: "Humorous",
    subTones: ["Witty", "Sarcastic", "Punny", "Light-hearted"]
  },
  {
    id: "luxurious",
    name: "Luxurious",
    subTones: ["Elegant", "Sophisticated", "Premium", "Exclusive"]
  }
];

// Aspect ratios
export const ASPECT_RATIOS = [
  { id: "1:1", name: "Square (1:1)", description: "Instagram Feed, Facebook" },
  { id: "16:9", name: "Landscape (16:9)", description: "YouTube, Web banners" },
  { id: "9:16", name: "Portrait (9:16)", description: "Reels, TikTok, Stories" }
];

// Platform character limits
export const PLATFORM_LIMITS = {
  X: 280,
  Instagram: 2200,
  LinkedIn: 3000,
  Facebook: 63206,
  TikTok: 2200
};

// Subscription plans in INR (for URL Campaign Analysis)
export const SUBSCRIPTION_PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    currency: "INR",
    period: "month",
    campaigns_per_month: 1,
    image_prompts: 3,
    features: ["1 campaign/month", "3 image prompts", "Basic copy", "No exports"],
    description: "Try it free"
  },
  {
    id: "starter",
    name: "Starter",
    price: 499,
    currency: "INR",
    period: "month",
    campaigns_per_month: 10,
    image_prompts: 5,
    features: ["10 campaigns/month", "5 image prompts each", "Full ad copy", "Platform variants", "Basic A/B testing"],
    description: "Best for trying premium features",
    daily_cost: "₹17/day"
  },
  {
    id: "pro",
    name: "Pro",
    price: 999,
    currency: "INR",
    period: "month",
    campaigns_per_month: -1,
    image_prompts: 5,
    popular: true,
    features: ["Unlimited campaigns", "Priority generation", "Advanced A/B testing", "Export to PDF/PNG", "Carousel formats"],
    description: "Best for creators & businesses",
    daily_cost: "₹33/day"
  },
  {
    id: "agency",
    name: "Agency",
    price: 2499,
    currency: "INR",
    period: "month",
    campaigns_per_month: -1,
    image_prompts: 5,
    features: ["Everything in Pro", "5 team members", "White-label", "WhatsApp support", "Custom brand voice", "API access"],
    description: "Best for agencies & brands"
  }
];

// Image generation plans (for AI Image feature)
export const CREDIT_PLANS = [
  { 
    id: "starter", 
    name: "Starter", 
    credits: 5, 
    price: 299, 
    currency: "INR",
    period: "month",
    features: ["5 AI ad images", "No watermark", "HD quality", "Download instantly"],
    description: "Best for trying premium features"
  },
  { 
    id: "creator", 
    name: "Creator", 
    credits: 20, 
    price: 599, 
    currency: "INR", 
    popular: true,
    period: "month",
    features: ["20 AI ad images", "No watermark", "HD quality", "Faster generation", "Priority support"],
    description: "Best for creators & small businesses"
  },
  { 
    id: "pro", 
    name: "Pro", 
    credits: 40, 
    price: 999, 
    currency: "INR",
    period: "month",
    features: ["40 AI ad images", "No watermark", "Highest quality", "Commercial use", "Priority support"],
    description: "Best for agencies & brands"
  }
];

// Affiliate commission tiers (30-45%)
export const AFFILIATE_TIERS = {
  bronze: { min_sales: 0, max_sales: 10, rate: 30 },
  silver: { min_sales: 11, max_sales: 50, rate: 35 },
  gold: { min_sales: 51, max_sales: 100, rate: 40 },
  platinum: { min_sales: 101, max_sales: Infinity, rate: 45 }
};

// Commission earnings per plan
export const AFFILIATE_COMMISSION_TABLE = [
  { plan: "Starter", price: 499, commission: 150 },
  { plan: "Pro", price: 999, commission: 300 },
  { plan: "Agency", price: 2499, commission: 750 }
];

// Affiliate commission rate (base)
export const AFFILIATE_COMMISSION_RATE = 30; // 30%

// Loading messages for generation
export const LOADING_MESSAGES = [
  "Analyzing user & product images...",
  "Detecting pose, lighting & composition...",
  "Blending product naturally with subject...",
  "Generating captions & hashtags...",
  "Creating optimized product link..."
];

// Image filters
export const IMAGE_FILTERS = [
  { id: "none", name: "None", filter: "" },
  { id: "sepia", name: "Sepia", filter: "sepia(100%)" },
  { id: "grayscale", name: "Grayscale", filter: "grayscale(100%)" },
  { id: "vintage", name: "Vintage", filter: "sepia(50%) contrast(90%) brightness(90%)" },
  { id: "warm", name: "Warm", filter: "sepia(30%) saturate(140%)" },
  { id: "cool", name: "Cool", filter: "saturate(80%) hue-rotate(20deg)" }
];
